import {BENEVA_CODE_SEPARATOR, BENEVA_INVESTMENT_ACCOUNT_CODE_PREFIX} from '@/constants/engine';
import {AccessRecord, DocumentModel, DocumentValue, ValueResolver} from '@/engine';
import {isFieldAccessRecord} from '@/engine/dependencies';
import {
  getReferenceType,
  isReferenceToAccount,
  isReferenceToComputedAllocationAccount,
  isReferenceToSourceAccount,
} from '@/engine/utils/reference';
import {SupportedLocale} from '@/initializers/i18next';
import {DomainValueReference, FIELD_TYPES, LocalizedString, Reference, TemplateValue} from '@/model/types';
import {ExcludeFirst} from '@/types/utils';

export const isEqual = (a?: DocumentValue, b?: DocumentValue): boolean => {
  if (typeof a !== typeof b) return false;

  if (typeof a === 'string') return a === b;

  if (!a) return !b;

  if ('$ref' in a) return a.$ref === (b as DomainValueReference).$ref;

  if ('$template' in a) return a.$template === (b as TemplateValue).$template;

  return a.en === (b as LocalizedString).en;
};

export const createSerializer = (valueResolver: ValueResolver) => {
  return function serializer(value: DocumentValue | DocumentValue[]): string {
    if (!value) return '';

    if (Array.isArray(value)) return value.map(serializer).join(',');

    if (typeof value === 'string') return value;

    if ('$ref' in value) return value.$ref;

    if ('$template' in value) return valueResolver(value);

    return value.en;
  };
};

export const createColumnResolver =
  (...args: ExcludeFirst<Parameters<typeof resolveAndFormatValue>>) =>
  (value: DocumentValue | undefined): string => {
    return resolveAndFormatValue(value, ...args);
  };

const resolveAndFormatValue = (
  value: DocumentValue | undefined,
  valueResolver: ValueResolver,
  locale: SupportedLocale,
  documentModel: DocumentModel,
  accessMap: Map<string, AccessRecord>,
  visibilitySet: Set<string>,
): string => {
  if (!value) return '';

  if (typeof value === 'string') return value;

  if ('$ref' in value && getReferenceType(value.$ref) === 'state') {
    const accessRecord = accessMap.get(value.$ref);

    if (!accessRecord) {
      // Predetermined allocations computed accounts and COMP fields are not part of the accessMap nor the visibilitySet
      if (
        isReferenceToComputedAllocationAccount(value.$ref) ||
        isReferenceToAccount(value.$ref) ||
        isReferenceToSourceAccount(value.$ref)
      ) {
        return valueResolver(value);
      }
      throw new Error(`Access record not found for ${value.$ref}`);
    }
    if (!isFieldAccessRecord(accessRecord)) throw new Error(`Access record ${value.$ref} is not a field access record`);

    const {sectionIndex, groupIndex, fieldIndex} = accessRecord;
    const groupModel = documentModel.sections[sectionIndex].groups[groupIndex];

    const referencedField =
      'rows' in groupModel ? groupModel.rows[0].fields[fieldIndex] : groupModel.fields[fieldIndex];
    let referencedFieldType = referencedField.type;

    if (referencedFieldType === FIELD_TYPES.HIDDEN) {
      if ('format' in referencedField && referencedField.format != undefined) {
        referencedFieldType = referencedField.format;
      }
    }

    const resolvedValue = valueResolver(value, {visibilitySet});

    switch (referencedFieldType) {
      case FIELD_TYPES.CURRENCY: {
        if (resolvedValue === '') return resolvedValue;
        const formatter = new Intl.NumberFormat(locale + '-CA', {
          style: 'currency',
          currency: 'CAD',
          maximumFractionDigits: 2,
        });
        return formatter.format(Number(resolvedValue));
      }
      case FIELD_TYPES.PERCENTAGE:
        return resolvedValue ? resolvedValue + ' %' : resolvedValue;
      default:
        resolvedValue;
    }
  }

  return valueResolver(value, {visibilitySet});
};

export const getBenevaInvestmentAccountCode = (
  account: Reference,
  feeType: Reference,
  valueResolver: ValueResolver,
): string => {
  if (!account || !feeType) return '';
  const codePrefix = valueResolver({$ref: BENEVA_INVESTMENT_ACCOUNT_CODE_PREFIX});
  const feeTypeCode = valueResolver({$ref: feeType.$ref, property: 'id_beneva'});
  const accountCode = valueResolver({$ref: account.$ref, property: 'id_beneva'});
  return `${codePrefix}${feeTypeCode}${BENEVA_CODE_SEPARATOR}${accountCode}`;
};

export const getBenevaGuaranteedInvestmentFundCode = (
  account: Reference,
  feeType: Reference,
  valueResolver: ValueResolver,
): string => {
  if (!account || !feeType) return '';
  const codePrefix = valueResolver({$ref: 'C13', property: 'id_beneva'});
  const feeTypeCode = valueResolver({$ref: feeType.$ref, property: 'id_beneva'});
  const accountCode = valueResolver({$ref: account.$ref, property: 'id_beneva'});
  return `${codePrefix}${feeTypeCode}${accountCode}`;
};
